import React, { useState } from 'react'
import {
  Box,
  Typography,
  IconButton,
  Chip,
  Button,
  CircularProgress,
  Select,
  MenuItem,
  FormControl,
} from '@mui/material'
import {
  ArrowLeft,
  ExternalLink,
  Heart,
  MessageCircle,
  Users,
  Star,
  CheckCircle,
  Telescope,
  ChevronDown,
  ChevronUp,
  Mic,
  Copy,
  Check as CheckIcon,
} from 'lucide-react'
import {
  useGetSuggestedProfilesQuery,
  useGetListeningTopicsQuery,
  useExploreAudienceMutation,
  useGetCreatorPostsLazyQuery,
  useGenerateGuestPitchMutation,
  useUpdateGuestStatusMutation,
  Platform,
} from 'graphql/generated/graphql'
import ContentFeed from 'pages/DiscoverContent/components/ContentFeed'
import InfoTooltip from 'components/InfoTooltip/InfoTooltip'

const platformIcons: Record<string, string> = {
  Instagram: '📷',
  TikTok: '🎵',
  Youtube: '🎥',
  Reddit: '🔴',
  Facebook: '📘',
  Twitter: '🐦',
  Bluesky: '🦋',
}

function formatNumber(num: number): string {
  if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`
  if (num >= 1000) return `${(num / 1000).toFixed(1)}K`
  return num.toString()
}

function formatDate(dateString: string | null): string {
  if (!dateString) return 'Never'
  const date = new Date(dateString)
  const now = new Date()
  const diffDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24))
  if (diffDays === 0) return 'Today'
  if (diffDays === 1) return 'Yesterday'
  if (diffDays < 7) return `${diffDays}d ago`
  return date.toLocaleDateString()
}

const GUEST_STATUSES = [
  { value: '', label: 'No status' },
  { value: 'contacted', label: '📩 Contacted' },
  { value: 'responded', label: '💬 Responded' },
  { value: 'booked', label: '📅 Booked' },
  { value: 'appeared', label: '✅ Appeared' },
]

function ProfileCard({ profile, rank }: { profile: any; rank: number }): JSX.Element {
  const [expanded, setExpanded] = useState(false)
  const [audience, setAudience] = useState<any[] | null>(null)
  const [showPosts, setShowPosts] = useState(false)
  const [creatorPosts, setCreatorPosts] = useState<any[] | null>(null)
  const [pitchText, setPitchText] = useState<string | null>(null)
  const [guestStatus, setGuestStatus] = useState<string>(profile.guestStatus ?? '')
  const [showPitch, setShowPitch] = useState(false)
  const [copied, setCopied] = useState(false)
  const [exploreAudience, { loading: exploring }] = useExploreAudienceMutation()
  const [fetchPosts, { loading: loadingPosts }] = useGetCreatorPostsLazyQuery()
  const [generatePitch, { loading: generatingPitch }] = useGenerateGuestPitchMutation()
  const [updateStatus] = useUpdateGuestStatusMutation()

  const handleStatusChange = (newStatus: string): void => {
    setGuestStatus(newStatus)
    updateStatus({ variables: { username: String(profile.authorUsername), status: newStatus || null } }).catch(console.error)
  }

  const handleGeneratePitch = async (): Promise<void> => {
    if (pitchText) {
      setShowPitch(!showPitch)
      return
    }
    try {
      const result = await generatePitch({
        variables: {
          authorUsername: String(profile.authorUsername),
          podcastName: 'my podcast', // TODO: get from user's podcast data
          podcastDescription: (profile.topicNames ?? []).join(', '),
        },
      })
      setPitchText(result.data?.generateGuestPitch ?? null)
      setShowPitch(true)
    } catch (error: any) {
      alert(error.message)
    }
  }

  const handleCopyPitch = (): void => {
    if (pitchText) {
      void navigator.clipboard.writeText(pitchText)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    }
  }

  const handleViewPosts = async (): Promise<void> => {
    if (creatorPosts) {
      setShowPosts(!showPosts)
      return
    }
    try {
      const result = await fetchPosts({
        variables: { username: String(profile.authorUsername), limit: 15 },
      })
      setCreatorPosts(result.data?.getCreatorPosts ?? [])
      setShowPosts(true)
    } catch (error: any) {
      alert(error.message)
    }
  }

  const handleExplore = async (): Promise<void> => {
    if (audience) {
      setExpanded(!expanded)
      return
    }
    try {
      const result = await exploreAudience({
        variables: { authorUsername: String(profile.authorUsername) },
      })
      setAudience(result.data?.exploreAudience ?? [])
      setExpanded(true)
    } catch (error: any) {
      alert(error.message)
    }
  }

  return (
    <Box
      sx={{
        backgroundColor: 'rgba(255,255,255,0.03)',
        border: '1px solid rgba(255,255,255,0.08)',
        borderRadius: '14px',
        overflow: 'hidden',
        transition: 'all 0.2s ease',
        '&:hover': {
          backgroundColor: 'rgba(255,255,255,0.05)',
          borderColor: 'rgba(168,85,247,0.2)',
        },
      }}
    >
      {/* Main row */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1.5, md: 2 }, p: { xs: 1.5, md: 2 } }}>
        <Typography sx={{ color: 'rgba(255,255,255,0.2)', fontWeight: 700, fontSize: '14px', width: 24, textAlign: 'center', flexShrink: 0 }}>
          {rank}
        </Typography>
        {profile.authorAvatarUrl ? (
          <Box component="img" src={profile.authorAvatarUrl} sx={{ width: { xs: 40, md: 48 }, height: { xs: 40, md: 48 }, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />
        ) : (
          <Box sx={{ width: { xs: 40, md: 48 }, height: { xs: 40, md: 48 }, borderRadius: '50%', backgroundColor: 'rgba(168,85,247,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <Typography sx={{ color: '#a855f7', fontWeight: 700, fontSize: '18px' }}>{String(profile.authorUsername).charAt(0).toUpperCase()}</Typography>
          </Box>
        )}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.25 }}>
            <Typography sx={{ color: 'white', fontWeight: 600, fontSize: { xs: '13px', md: '14px' }, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              @{profile.authorUsername}
            </Typography>
            {profile.primaryPlatform && <Typography sx={{ fontSize: '12px' }}>{platformIcons[profile.primaryPlatform] ?? ''}</Typography>}
          </Box>
          {/* Metrics row */}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: { xs: 1, md: 1.5 }, alignItems: 'center', mb: 0.5 }}>
            {profile.followerCount != null && profile.followerCount > 0 && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Users size={11} color="rgba(255,255,255,0.4)" />
                <Typography sx={{ fontSize: '11px', color: 'rgba(255,255,255,0.5)' }}>
                  {formatNumber(profile.followerCount)} followers
                </Typography>
              </Box>
            )}
            {profile.engagementRate != null && profile.engagementRate > 0 && (
              <Typography sx={{ fontSize: '11px', fontWeight: 600, color: profile.engagementRate >= 3 ? '#22c55e' : profile.engagementRate >= 1 ? '#eab308' : 'rgba(255,255,255,0.4)' }}>
                {String(profile.engagementRate)}% ER
              </Typography>
            )}
            {profile.avgLikes > 0 && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Heart size={11} color="rgba(255,255,255,0.4)" />
                <Typography sx={{ fontSize: '11px', color: 'rgba(255,255,255,0.5)' }}>
                  {formatNumber(profile.avgLikes)} avg likes
                </Typography>
              </Box>
            )}
            {profile.avgComments > 0 && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <MessageCircle size={11} color="rgba(255,255,255,0.4)" />
                <Typography sx={{ fontSize: '11px', color: 'rgba(255,255,255,0.5)' }}>
                  {formatNumber(profile.avgComments)} avg comments
                </Typography>
              </Box>
            )}
            {profile.postCount > 1 && (
              <Typography sx={{ fontSize: '11px', color: 'rgba(255,255,255,0.3)' }}>
                {String(profile.postCount)} scraped
              </Typography>
            )}
            {profile.lastPostedAt && (
              <Typography sx={{ fontSize: '11px', color: 'rgba(255,255,255,0.3)' }}>
                Active {formatDate(profile.lastPostedAt)}
              </Typography>
            )}
          </Box>
          {/* Topic tags */}
          {profile.topicNames && profile.topicNames.length > 0 && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {profile.topicNames.slice(0, 3).map((name: string) => (
                <Chip
                  key={name}
                  label={name}
                  size="small"
                  sx={{ height: 18, fontSize: '10px', backgroundColor: 'rgba(168,85,247,0.08)', color: 'rgba(168,85,247,0.7)', border: 'none' }}
                />
              ))}
            </Box>
          )}
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5, flexShrink: 0 }}>
          {profile.timesEngaged > 0 ? (
            <Chip icon={<CheckCircle size={12} />} label={`Engaged ${String(profile.timesEngaged)}x`} size="small" sx={{ backgroundColor: 'rgba(34,197,94,0.1)', border: '1px solid rgba(34,197,94,0.2)', color: '#22c55e', fontSize: '11px', height: 24, '& .MuiChip-icon': { color: '#22c55e' } }} />
          ) : profile.discoveredVia ? (
            <Chip
              icon={<Telescope size={12} />}
              label={`via @${String(profile.discoveredVia)}`}
              size="small"
              sx={{ backgroundColor: 'rgba(59,130,246,0.1)', border: '1px solid rgba(59,130,246,0.2)', color: '#3b82f6', fontSize: '11px', height: 24, '& .MuiChip-icon': { color: '#3b82f6' } }}
            />
          ) : (
            <Chip icon={<Star size={12} />} label="New" size="small" sx={{ backgroundColor: 'rgba(168,85,247,0.1)', border: '1px solid rgba(168,85,247,0.2)', color: '#a855f7', fontSize: '11px', height: 24, '& .MuiChip-icon': { color: '#a855f7' } }} />
          )}
          {profile.commentFrequency != null && profile.commentFrequency > 0 && profile.discoveredVia && (
            <Typography sx={{ fontSize: '10px', color: 'rgba(59,130,246,0.6)' }}>
              {String(profile.commentFrequency)} comments found
            </Typography>
          )}
          {profile.isGuestCandidate && (
            <select
              value={guestStatus}
              onChange={(e) => handleStatusChange(e.target.value)}
              style={{
                background: guestStatus === 'appeared' ? 'rgba(34,197,94,0.15)' : guestStatus === 'booked' ? 'rgba(59,130,246,0.15)' : 'rgba(245,158,11,0.1)',
                border: `1px solid ${guestStatus === 'appeared' ? 'rgba(34,197,94,0.3)' : guestStatus === 'booked' ? 'rgba(59,130,246,0.3)' : 'rgba(245,158,11,0.2)'}`,
                color: guestStatus === 'appeared' ? '#22c55e' : guestStatus === 'booked' ? '#3b82f6' : '#f59e0b',
                borderRadius: '6px',
                fontSize: '10px',
                fontWeight: 600,
                padding: '2px 6px',
                cursor: 'pointer',
                outline: 'none',
              }}
            >
              {GUEST_STATUSES.map((s) => (
                <option key={s.value} value={s.value}>{s.value ? s.label : '🎤 Potential Guest'}</option>
              ))}
            </select>
          )}
        </Box>
        {/* Pitch button for guest candidates */}
        {profile.isGuestCandidate && (
          <Button
            onClick={() => { handleGeneratePitch().catch(console.error) }}
            disabled={generatingPitch}
            size="small"
            startIcon={generatingPitch ? <CircularProgress size={10} /> : <Mic size={10} />}
            sx={{
              color: '#f59e0b',
              border: '1px solid rgba(245,158,11,0.2)',
              borderRadius: '8px',
              textTransform: 'none',
              fontSize: '11px',
              px: 1,
              flexShrink: 0,
              '&:hover': { backgroundColor: 'rgba(245,158,11,0.1)' },
            }}
          >
            {generatingPitch ? '' : pitchText ? 'Pitch' : 'Write Pitch'}
          </Button>
        )}
        {/* View Posts + Explore Audience buttons */}
        {String(profile.primaryPlatform) === 'Instagram' && (
          <>
            <Button
              onClick={() => { handleViewPosts().catch(console.error) }}
              disabled={loadingPosts}
              size="small"
              startIcon={loadingPosts ? <CircularProgress size={12} /> : <MessageCircle size={12} />}
              sx={{
                color: 'rgba(255,255,255,0.6)',
                border: '1px solid rgba(255,255,255,0.1)',
                borderRadius: '8px',
                textTransform: 'none',
                fontSize: '11px',
                px: 1,
                flexShrink: 0,
                '&:hover': { backgroundColor: 'rgba(255,255,255,0.05)' },
              }}
            >
              {loadingPosts ? '' : showPosts ? 'Hide' : 'Posts'}
            </Button>
            <Button
              onClick={() => { handleExplore().catch(console.error) }}
              disabled={exploring}
              size="small"
              startIcon={exploring ? <CircularProgress size={12} /> : <Telescope size={12} />}
              endIcon={audience ? (expanded ? <ChevronUp size={12} /> : <ChevronDown size={12} />) : undefined}
              sx={{
                color: '#a855f7',
                border: '1px solid rgba(168,85,247,0.2)',
                borderRadius: '8px',
                textTransform: 'none',
                fontSize: '11px',
                px: 1,
                flexShrink: 0,
                '&:hover': { backgroundColor: 'rgba(168,85,247,0.1)' },
              }}
            >
              {exploring ? '' : audience ? 'Listeners' : 'Find Listeners'}
            </Button>
          </>
        )}
        {profile.latestContentUrl && (
          <IconButton size="small" onClick={() => window.open(profile.latestContentUrl, '_blank')} sx={{ color: 'rgba(255,255,255,0.3)', flexShrink: 0, '&:hover': { color: '#a855f7' } }}>
            <ExternalLink size={14} />
          </IconButton>
        )}
      </Box>

      {/* Expanded audience section */}
      {expanded && audience && audience.length > 0 && (
        <Box sx={{ px: 2, pb: 2, borderTop: '1px solid rgba(255,255,255,0.05)' }}>
          <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', mt: 1.5, mb: 1 }}>
            Potential listeners from @{profile.authorUsername}
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {audience.map((member: any) => (
              <Box
                key={member.username}
                onClick={() => window.open(`https://www.instagram.com/${String(member.username)}/`, '_blank')}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  p: 1,
                  backgroundColor: 'rgba(255,255,255,0.03)',
                  border: '1px solid rgba(255,255,255,0.06)',
                  borderRadius: '10px',
                  cursor: 'pointer',
                  transition: 'all 0.15s',
                  '&:hover': { backgroundColor: 'rgba(168,85,247,0.08)', borderColor: 'rgba(168,85,247,0.2)' },
                }}
              >
                {member.profilePicUrl ? (
                  <Box component="img" src={member.profilePicUrl} sx={{ width: 28, height: 28, borderRadius: '50%', objectFit: 'cover' }} />
                ) : (
                  <Box sx={{ width: 28, height: 28, borderRadius: '50%', backgroundColor: 'rgba(168,85,247,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Typography sx={{ fontSize: '12px', color: '#a855f7', fontWeight: 600 }}>{String(member.username).charAt(0).toUpperCase()}</Typography>
                  </Box>
                )}
                <Box>
                  <Typography sx={{ color: 'white', fontSize: '12px', fontWeight: 600 }}>@{member.username}</Typography>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    {member.followerCount != null && (
                      <Typography sx={{ fontSize: '10px', color: 'rgba(255,255,255,0.4)' }}>{formatNumber(member.followerCount)}</Typography>
                    )}
                    <Typography sx={{ fontSize: '10px', color: 'rgba(168,85,247,0.7)' }}>
                      {String(member.commentFrequency)} {member.commentFrequency === 1 ? 'comment' : 'comments'}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      )}
      {expanded && audience && audience.length === 0 && (
        <Box sx={{ px: 2, pb: 2, borderTop: '1px solid rgba(255,255,255,0.05)' }}>
          <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '12px', mt: 1.5, textAlign: 'center' }}>
            No listener data available for this creator yet.
          </Typography>
        </Box>
      )}

      {/* Guest pitch section */}
      {showPitch && pitchText && (
        <Box sx={{ px: 2, pb: 2, borderTop: '1px solid rgba(245,158,11,0.15)' }}>
          <Typography sx={{ color: 'rgba(245,158,11,0.7)', fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', mt: 1.5, mb: 1 }}>
            Guest pitch for @{profile.authorUsername}
          </Typography>
          <Box sx={{ p: 1.5, backgroundColor: 'rgba(245,158,11,0.05)', borderRadius: '10px', border: '1px solid rgba(245,158,11,0.1)', mb: 1 }}>
            <Typography sx={{ color: 'rgba(255,255,255,0.8)', fontSize: '13px', lineHeight: 1.6 }}>
              {pitchText}
            </Typography>
          </Box>
          <Button
            onClick={handleCopyPitch}
            size="small"
            startIcon={copied ? <CheckIcon size={12} /> : <Copy size={12} />}
            sx={{ color: copied ? '#22c55e' : 'rgba(255,255,255,0.6)', textTransform: 'none', fontSize: '12px' }}
          >
            {copied ? 'Copied' : 'Copy pitch'}
          </Button>
        </Box>
      )}

      {/* Creator posts section */}
      {showPosts && creatorPosts && creatorPosts.length > 0 && (
        <Box sx={{ px: 2, pb: 2, borderTop: '1px solid rgba(255,255,255,0.05)' }}>
          <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', mt: 1.5, mb: 1 }}>
            Recent posts by @{profile.authorUsername}
          </Typography>
          <Box sx={{ '& > div': { columnCount: '1 !important' } }}>
            <ContentFeed content={creatorPosts} />
          </Box>
        </Box>
      )}
      {showPosts && creatorPosts && creatorPosts.length === 0 && (
        <Box sx={{ px: 2, pb: 2, borderTop: '1px solid rgba(255,255,255,0.05)' }}>
          <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '12px', mt: 1.5, textAlign: 'center' }}>
            No public posts found for this creator.
          </Typography>
        </Box>
      )}
    </Box>
  )
}

interface SuggestedProfilesProps {
  onBack: () => void
}

const FOLLOWER_PRESETS = [
  { label: 'All', min: 0, max: 0 },
  { label: 'Starter (0-1K)', min: 1, max: 1000 },
  { label: 'Nano (1K-10K)', min: 1000, max: 10000 },
  { label: 'Micro (10K-50K)', min: 10000, max: 50000 },
  { label: 'Mid (50K-500K)', min: 50000, max: 500000 },
  { label: '500K+', min: 500000, max: 0 },
]

export default function SuggestedProfiles({ onBack }: SuggestedProfilesProps): JSX.Element {
  const [selectedTopicId, setSelectedTopicId] = useState<string>('')
  const [followerMin, setFollowerMin] = useState(0)
  const [followerMax, setFollowerMax] = useState(0)
  const [offset, setOffset] = useState(0)
  const ITEMS_PER_PAGE = 30

  const { data: topicsData } = useGetListeningTopicsQuery()
  const topics = topicsData?.getListeningTopics ?? []

  const { data, loading } = useGetSuggestedProfilesQuery({
    variables: {
      topicId: selectedTopicId || undefined,
      limit: 200, // Fetch more for client-side filtering
      offset: 0,
    },
  })

  // Client-side follower filtering
  const allProfiles = data?.getSuggestedProfiles?.profiles ?? []
  const filteredProfiles = allProfiles.filter((p: any) => {
    if (followerMin === 0 && followerMax === 0) return true // "All" shows everything
    const fc = p.followerCount ?? 0
    if (fc === 0) return false // Exclude unknowns when a range is selected
    if (followerMin > 0 && fc < followerMin) return false
    if (followerMax > 0 && fc > followerMax) return false
    return true
  })
  const profiles = filteredProfiles.slice(offset, offset + ITEMS_PER_PAGE)
  const totalCount = filteredProfiles.length

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, mb: 3 }}>
        <IconButton onClick={onBack} sx={{ color: 'rgba(255,255,255,0.7)' }}>
          <ArrowLeft size={20} />
        </IconButton>
        <Typography sx={{ fontWeight: 700, color: 'white', flex: 1, fontSize: { xs: '18px', md: '24px' } }}>
          Your Network<InfoTooltip text="Creators and potential guests discovered from your Social Radar signals. Ranked by engagement and relevance to your topics." />
        </Typography>
        <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '13px' }}>
          {totalCount} connections
        </Typography>
      </Box>

      {/* Topic filter */}
      <Box sx={{ mb: 3 }}>
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <Select
            value={selectedTopicId}
            onChange={(e) => { setSelectedTopicId(e.target.value); setOffset(0) }}
            displayEmpty
            sx={{
              color: 'white',
              backgroundColor: 'rgba(255,255,255,0.05)',
              borderRadius: '10px',
              fontSize: '13px',
              '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.1)' },
              '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(168,85,247,0.3)' },
              '& .MuiSelect-icon': { color: 'rgba(255,255,255,0.4)' },
            }}
          >
            <MenuItem value="" sx={{ fontSize: '13px' }}>All Topics</MenuItem>
            {topics.map((t) => (
              <MenuItem key={t.id} value={t.id} sx={{ fontSize: '13px' }}>{t.name}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* Follower range filter */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 3, alignItems: 'center' }}>
        <Users size={14} color="rgba(255,255,255,0.4)" />
        {FOLLOWER_PRESETS.map((preset) => (
          <Chip
            key={preset.label}
            label={preset.label}
            size="small"
            onClick={() => { setFollowerMin(preset.min); setFollowerMax(preset.max); setOffset(0) }}
            sx={{
              backgroundColor: followerMin === preset.min && followerMax === preset.max
                ? 'rgba(168,85,247,0.25)'
                : 'rgba(255,255,255,0.05)',
              border: followerMin === preset.min && followerMax === preset.max
                ? '1px solid rgba(168,85,247,0.4)'
                : '1px solid rgba(255,255,255,0.1)',
              color: followerMin === preset.min && followerMax === preset.max
                ? 'white'
                : 'rgba(255,255,255,0.6)',
              fontSize: '12px',
              '&:hover': { backgroundColor: 'rgba(168,85,247,0.15)' },
            }}
          />
        ))}
      </Box>

      {/* Loading */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress sx={{ color: '#a855f7' }} />
        </Box>
      )}

      {/* Empty state */}
      {!loading && profiles.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '16px' }}>
            Your network is empty. Set up a Social Radar topic to start discovering creators and potential guests.
          </Typography>
        </Box>
      )}

      {/* Profile cards */}
      {!loading && profiles.length > 0 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {profiles.map((profile: any, i: number) => (
            <ProfileCard key={String(profile.authorUsername)} profile={profile} rank={offset + i + 1} />
          ))}

          {/* Pagination */}
          {totalCount > ITEMS_PER_PAGE && (
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 2 }}>
              <Button
                onClick={() => setOffset(Math.max(0, offset - ITEMS_PER_PAGE))}
                disabled={offset === 0}
                sx={{ color: 'rgba(255,255,255,0.5)', textTransform: 'none', fontSize: '13px' }}
              >
                Previous
              </Button>
              <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '13px', lineHeight: '36px' }}>
                {offset + 1}-{Math.min(offset + ITEMS_PER_PAGE, totalCount)} of {totalCount}
              </Typography>
              <Button
                onClick={() => setOffset(offset + ITEMS_PER_PAGE)}
                disabled={offset + ITEMS_PER_PAGE >= totalCount}
                sx={{ color: 'rgba(255,255,255,0.5)', textTransform: 'none', fontSize: '13px' }}
              >
                Next
              </Button>
            </Box>
          )}
        </Box>
      )}
    </Box>
  )
}
